/**
 * Legacy manual RXNT portal login (auth.rxnt.com). Prefer RxntLaunchButton + Integration SDK
 * (GET /rxnt/launch/token + /launch/context) for in-app EHR launch — no user password/SSO in Snap UI.
 *
 * Official RXNT web app entry. Opens RXNT’s login flow; users choose RXNT credentials or
 * **Sign in with Microsoft** (when their practice has SSO configured in RXNT).
 *
 * Do **not** navigate to {@link RXNT_DOCUMENTED_OAUTH_REDIRECT_URI} from a button — that URL is
 * only the OAuth **redirect_uri** registered in Azure for RXNT (see RXNT Help:
 * Set Up Microsoft SSO on Login). It is not an entry point and will error without a full `/oauth2/authorize` flow.
 */
export const RXNT_PORTAL_URL = 'https://auth.rxnt.com/auth/login';

/** Documented Azure redirect URI for RXNT SSO setup — for documentation / Azure config only, not for window.open */
export const RXNT_DOCUMENTED_OAUTH_REDIRECT_URI = 'https://auth.rxnt.com/oauth2/callback';

/**
 * Normalize RXNT locations list from API.
 * Backend sync returns { locations: { data: [...], total, hasData } } from RXNT;
 * also supports bare arrays or { locations: [...] }.
 */
export function normalizeRxntLocations(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.locations)) return payload.locations;
  if (payload.locations && Array.isArray(payload.locations.data)) {
    return payload.locations.data;
  }
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeRxntDoctors(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
}

/** Snap statuses that {@link canBeVoided} allows on the API. */
const VOIDABLE_LOCAL_STATUSES = new Set(['PENDING', 'SENT']);

export function rowWorklistId(item, activeTab) {
  return String(item?.worklist || activeTab || '').toLowerCase();
}

export function isVoidableLocalStatus(status) {
  return VOIDABLE_LOCAL_STATUSES.has(String(status || '').toUpperCase());
}

/** Linked Snap prescription row with a voidable status. */
export function canShowVoidForLocalRecord(local) {
  if (!local?.id) return false;
  return isVoidableLocalStatus(local.status);
}

/**
 * Results RXNT worklist row: void only on gateway `worklist: "pending"` with a linked Snap record.
 */
export function canShowVoidForGatewayRow(item, activeTab) {
  if (rowWorklistId(item, activeTab) !== 'pending') return false;
  return canShowVoidForLocalRecord(item?.local);
}

/**
 * EMR visit prescription list: void when PENDING, or SENT and still on RXNT pending worklist.
 * @param {Set<number>|null|undefined} pendingRxntPrescriptionIds — null while loading / unavailable
 */
export function canShowVoidForVisitPrescription(prescription, pendingRxntPrescriptionIds) {
  if (!prescription?.id) return false;
  const st = String(prescription.status || '').toUpperCase();
  if (st === 'PENDING') return true;
  if (st !== 'SENT') return false;
  const rxId = prescription.rxnt_prescription_id;
  if (rxId == null) return false;
  if (pendingRxntPrescriptionIds == null) return false;
  return pendingRxntPrescriptionIds.has(Number(rxId));
}

/** Build a Set of RXNT prescription ids from gateway pending worklist API rows. */
export function pendingRxntPrescriptionIdSet(worklistRows) {
  const ids = new Set();
  if (!Array.isArray(worklistRows)) return ids;
  worklistRows.forEach((item) => {
    const rxId = item?.rx?.prescriptionId ?? item?.local?.rxnt_prescription_id;
    if (rxId != null) ids.add(Number(rxId));
  });
  return ids;
}

export const emptyPharmacyFilters = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
};

export function getPharmacyPhone(p) {
  return p?.contact?.phone ?? p?.phone ?? p?.phoneNumber ?? '';
}

export function getPharmacyFax(p) {
  return p?.contact?.fax ?? p?.fax ?? p?.faxNumber ?? '';
}

export function formatPharmacyAddress(pharmacy) {
  if (!pharmacy) return '';
  if (typeof pharmacy.address === 'string' && pharmacy.address) {
    return pharmacy.address;
  }
  const nest =
    pharmacy.address && typeof pharmacy.address === 'object' ? pharmacy.address : {};
  const parts = [
    nest.address1 ?? pharmacy.address1,
    typeof pharmacy.address === 'string' ? pharmacy.address : null,
    nest.city ?? pharmacy.city,
    nest.state ?? pharmacy.state,
    nest.zip ?? pharmacy.zipCode ?? pharmacy.zip,
  ].filter(Boolean);
  return parts.join(', ');
}

/** Build RXNT-shaped pharmacy object from employee preferred-pharmacy columns. */
export function selectedPharmacyFromEmployee(employee) {
  const id = employee?.preferred_rxnt_pharmacy_id;
  if (id == null || id === '') return null;
  return {
    pharmacyId: Number(id),
    name: employee.preferred_pharmacy_name || '',
    pharmacyName: employee.preferred_pharmacy_name || '',
    address: employee.preferred_pharmacy_address || '',
    contact: {
      phone: employee.preferred_pharmacy_phone || '',
      fax: employee.preferred_pharmacy_fax || '',
    },
  };
}

export function pharmacyFiltersFromEmployee(employee) {
  if (employee?.preferred_rxnt_pharmacy_id == null || employee?.preferred_rxnt_pharmacy_id === '') {
    return { ...emptyPharmacyFilters };
  }
  return pharmacyFiltersFromSelectedPharmacy(selectedPharmacyFromEmployee(employee));
}

/** Parse "street, city, ST, zip" saved from formatPharmacyAddress into filter fields. */
export function parseFormattedPharmacyAddress(addressStr) {
  const empty = { address: '', city: '', state: '', zip: '' };
  if (!addressStr || typeof addressStr !== 'string') return empty;

  const parts = addressStr.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return empty;
  if (parts.length === 1) return { ...empty, address: parts[0] };

  const remaining = [...parts];
  const last = remaining[remaining.length - 1];
  let zip = '';
  let state = '';

  const stateZipMatch = last.match(/^([A-Za-z]{2})\s+(\d{5,9})$/);
  if (stateZipMatch) {
    state = stateZipMatch[1].toUpperCase();
    zip = stateZipMatch[2];
    remaining.pop();
  } else if (/^\d{5,9}$/.test(last.replace(/\D/g, ''))) {
    zip = last.replace(/\D/g, '').slice(0, 9);
    remaining.pop();
    if (remaining.length > 0 && /^[A-Za-z]{2}$/.test(remaining[remaining.length - 1])) {
      state = remaining.pop().toUpperCase();
    }
  } else if (/^[A-Za-z]{2}$/.test(last)) {
    state = last.toUpperCase();
    remaining.pop();
  }

  if (remaining.length === 0) {
    return { address: '', city: '', state, zip };
  }
  if (remaining.length === 1) {
    return { address: remaining[0], city: '', state, zip };
  }

  const city = remaining[remaining.length - 1];
  const address = remaining.slice(0, -1).join(', ');
  return { address, city, state, zip };
}

/** Populate search filters from any selected pharmacy shape (RXNT gateway or employee saved). */
export function pharmacyFiltersFromSelectedPharmacy(pharmacy) {
  if (!pharmacy) return { ...emptyPharmacyFilters };

  if (pharmacy.address && typeof pharmacy.address === 'object') {
    return pharmacyFiltersFromGatewayPharmacy(pharmacy);
  }

  const contact =
    pharmacy?.contact && typeof pharmacy.contact === 'object' ? pharmacy.contact : {};
  const parsed = parseFormattedPharmacyAddress(
    typeof pharmacy.address === 'string' ? pharmacy.address : ''
  );

  return {
    name: pharmacy?.name || pharmacy?.pharmacyName || '',
    address: parsed.address,
    city: parsed.city,
    state: parsed.state,
    zip: parsed.zip,
    phone: String(contact?.phone || getPharmacyPhone(pharmacy) || '')
      .replace(/\D/g, '')
      .slice(0, 10),
    fax: String(contact?.fax || getPharmacyFax(pharmacy) || '')
      .replace(/\D/g, '')
      .slice(0, 10),
  };
}

export function pharmacyFiltersFromGatewayPharmacy(pharmacy) {
  const address = pharmacy?.address && typeof pharmacy.address === 'object' ? pharmacy.address : {};
  const contact = pharmacy?.contact && typeof pharmacy.contact === 'object' ? pharmacy.contact : {};
  return {
    name: pharmacy?.name || pharmacy?.pharmacyName || '',
    address: address?.address1 || '',
    city: address?.city || '',
    state: String(address?.state || '').toUpperCase().slice(0, 2),
    zip: String(address?.zip || '').replace(/\D/g, '').slice(0, 9),
    phone: String(contact?.phone || getPharmacyPhone(pharmacy) || '').replace(/\D/g, '').slice(0, 10),
    fax: String(contact?.fax || getPharmacyFax(pharmacy) || '').replace(/\D/g, '').slice(0, 10),
  };
}

export function employeePreferredPharmacyFieldsFromGateway(pharmacy) {
  const address = pharmacy?.address && typeof pharmacy.address === 'object' ? pharmacy.address : {};
  const contact = pharmacy?.contact && typeof pharmacy.contact === 'object' ? pharmacy.contact : {};
  const pharmacyId = pharmacy?.pharmacyId ?? pharmacy?.id;
  return {
    preferred_rxnt_pharmacy_id: pharmacyId != null ? String(pharmacyId) : '',
    preferred_pharmacy_name: pharmacy?.name || pharmacy?.pharmacyName || '',
    preferred_pharmacy_address: formatPharmacyAddress(pharmacy) || '',
    preferred_pharmacy_phone: String(contact?.phone || getPharmacyPhone(pharmacy) || '').replace(/\D/g, '').slice(0, 10),
    preferred_pharmacy_fax: String(contact?.fax || getPharmacyFax(pharmacy) || '').replace(/\D/g, '').slice(0, 10),
  };
}

/** User-facing text from Snap/RXNT prescription API error payloads. */
export function extractRxntPrescriptionErrorMessage(res, error, drugName = '') {
  const raw =
    res?.message ||
    error?.data?.message ||
    error?.response?.data?.message ||
    (typeof error === 'string' ? error : error?.message) ||
    '';

  if (!raw) {
    return drugName
      ? `Failed to send ${drugName}`
      : 'Failed to send prescription';
  }

  let text = String(raw);
  text = text.replace(/^Failed to create prescription:\s*/i, '');
  text = text.replace(/^Failed to send prescription to RXNT:\s*/i, '');
  text = text.replace(/^RXNT API request failed:\s*/i, '');

  const pipeParts = text.split(' | ').map((p) => p.trim()).filter(Boolean);
  if (pipeParts.length > 1) {
    const detail = pipeParts[pipeParts.length - 1];
    const fieldMatch = detail.match(/^[\w\[\].]+:\s*(.+)$/);
    return fieldMatch ? fieldMatch[1].trim() : detail;
  }

  const fieldMatch = text.match(/StructuredSigs\[\d+\]\.\w+:\s*(.+)$/i);
  if (fieldMatch) {
    return fieldMatch[1].trim();
  }

  return text.trim();
}

export function clearEmployeePreferredPharmacyFields() {
  return {
    preferred_rxnt_pharmacy_id: '',
    preferred_pharmacy_name: '',
    preferred_pharmacy_address: '',
    preferred_pharmacy_phone: '',
    preferred_pharmacy_fax: '',
  };
}
